import React, { useMemo } from "react";
import SettingsSection from "../SettingsSection";
import ChannelPicker from "../ChannelPicker";
import RolePicker from "../RolePicker";
import {
  DEFAULT_QOTD_QUESTIONS,
  QOTD_SOURCES,
  normalizeQotdSource,
} from "../../data/qotdDefaults";

const TIMEZONES = [
  "America/Chicago",
  "America/New_York",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Australia/Sydney",
];

const HOURS = Array.from({ length: 24 }, (_, hour) => ({
  value: hour,
  label:
    hour === 0
      ? "12:00 AM"
      : hour < 12
        ? `${hour}:00 AM`
        : hour === 12
          ? "12:00 PM"
          : `${hour - 12}:00 PM`,
}));

export default function QotdSection({ channels, roles, qotd, setQotd }) {
  const update = (patch) => setQotd((prev) => ({ ...prev, ...patch }));
  const source = normalizeQotdSource(qotd.source);
  const showCustom = source === "custom" || source === "both";

  const questionText = useMemo(
    () => (Array.isArray(qotd.questions) ? qotd.questions.join("\n") : ""),
    [qotd.questions],
  );

  const customCount = (qotd.questions || []).length;
  const defaultCount = DEFAULT_QOTD_QUESTIONS.length;
  const activeCount =
    source === "custom"
      ? customCount
      : source === "default"
        ? defaultCount
        : customCount + defaultCount;

  return (
    <SettingsSection>
      <p className="help-text" style={{ marginBottom: "15px" }}>
        Post a random question once per day. Kami ships with {defaultCount}{" "}
        default prompts - you can use those alone, your own list, or both.
        Questions won&apos;t repeat until the whole active list has been used.
        Saving settings will not re-post today&apos;s question.
      </p>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          marginBottom: "20px",
        }}
      >
        <input
          type="checkbox"
          checked={qotd.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
        />
        <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
          ENABLE QUESTION OF THE DAY
        </span>
      </label>

      <label className="input-label">CHANNEL</label>
      <ChannelPicker
        channels={channels}
        value={qotd.channelId}
        onChange={(v) => update({ channelId: v })}
        types={[0]}
        emptyLabel="None"
      />

      <label className="input-label" style={{ marginTop: "15px" }}>
        PING ROLE (OPTIONAL)
      </label>
      <RolePicker
        roles={roles}
        value={qotd.pingRoleId}
        onChange={(v) => update({ pingRoleId: v })}
        emptyLabel="None"
        placeholder="Select ping role…"
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          marginTop: "15px",
        }}
      >
        <div>
          <label className="input-label">TIMEZONE</label>
          <select
            value={qotd.timezone}
            onChange={(e) => update({ timezone: e.target.value })}
            className="settings-input"
          >
            {!TIMEZONES.includes(qotd.timezone) && qotd.timezone ? (
              <option value={qotd.timezone}>{qotd.timezone}</option>
            ) : null}
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="input-label">POST AFTER</label>
          <select
            value={qotd.postHour}
            onChange={(e) => update({ postHour: Number(e.target.value) })}
            className="settings-input"
          >
            {HOURS.map((hour) => (
              <option key={hour.value} value={hour.value}>
                {hour.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="help-text" style={{ marginTop: "8px" }}>
        Kami checks every 15 minutes and posts once the local time reaches this
        hour.
      </p>

      <label className="input-label" style={{ marginTop: "15px" }}>
        EMBED TITLE
      </label>
      <input
        type="text"
        value={qotd.title}
        onChange={(e) => update({ title: e.target.value })}
        className="settings-input"
        maxLength={256}
      />

      <label className="input-label" style={{ marginTop: "15px" }}>
        QUESTION SOURCE
      </label>
      <select
        value={source}
        onChange={(e) => update({ source: e.target.value })}
        className="settings-input"
      >
        {QOTD_SOURCES.map((entry) => (
          <option key={entry.value} value={entry.value}>
            {entry.label}
          </option>
        ))}
      </select>
      <p className="help-text" style={{ marginTop: "8px" }}>
        Active pool: {activeCount} question{activeCount === 1 ? "" : "s"}
        {source === "both"
          ? ` (${customCount} custom + ${defaultCount} default)`
          : source === "default"
            ? ` (${defaultCount} built-in)`
            : ` (${customCount} custom)`}
      </p>

      {showCustom ? (
        <>
          <label className="input-label" style={{ marginTop: "15px" }}>
            CUSTOM QUESTIONS (ONE PER LINE)
          </label>
          <textarea
            value={questionText}
            onChange={(e) =>
              update({
                questions: e.target.value
                  .split("\n")
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .slice(0, 200),
              })
            }
            className="settings-input large-text"
            rows={10}
            placeholder={
              "What's your favorite comfort food?\nIf you could teleport anywhere right now, where would you go?"
            }
          />
          <p className="help-text" style={{ marginTop: "8px" }}>
            {customCount} custom question{customCount === 1 ? "" : "s"} · max
            200 · blank lines ignored
          </p>
        </>
      ) : (
        <p className="help-text" style={{ marginTop: "15px" }}>
          Using the built-in pack only. Switch to Custom or Custom + default to
          add your own prompts.
        </p>
      )}
    </SettingsSection>
  );
}
